import { useState, useEffect, useRef } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Upload, FileText, Search, Download, Eye, FolderArchive, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminStatements() {
  const [statements, setStatements] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewType, setPreviewType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [uploadData, setUploadData] = useState({
    driver_id: '',
    period_start: '',
    period_end: '',
    pay_run_reference: '',
    tripsheet_reference: '',
    notes: '',
    amount: '',
    file: null
  });
  const [bulkUploadData, setBulkUploadData] = useState({
    period_start: '',
    period_end: '',
    file: null
  });

  useEffect(() => {
    Promise.all([fetchStatements(), fetchDrivers()]);
  }, []);

  const fetchStatements = async () => {
    try {
      const response = await axios.get(`${API_URL}/statements`);
      setStatements(response.data);
    } catch (error) {
      toast.error('Failed to load statements');
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API_URL}/drivers`);
      setDrivers(response.data.filter(d => d.is_active));
    } catch (error) {
      console.error('Failed to load drivers');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadData.file) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('driver_id', uploadData.driver_id);
      formData.append('period_start', uploadData.period_start);
      formData.append('period_end', uploadData.period_end);
      formData.append('file', uploadData.file);
      
      if (uploadData.pay_run_reference) formData.append('pay_run_reference', uploadData.pay_run_reference);
      if (uploadData.tripsheet_reference) formData.append('tripsheet_reference', uploadData.tripsheet_reference);
      if (uploadData.notes) formData.append('notes', uploadData.notes);
      if (uploadData.amount) formData.append('amount', uploadData.amount);
      
      await axios.post(`${API_URL}/statements/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Statement uploaded successfully. Driver notified!');
      setShowUploadDialog(false);
      setUploadData({
        driver_id: '',
        period_start: '',
        period_end: '',
        pay_run_reference: '',
        tripsheet_reference: '',
        notes: '',
        amount: '',
        file: null
      });
      fetchStatements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload statement');
    } finally {
      setUploading(false);
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    
    if (!bulkUploadData.file) {
      toast.error('Please select a ZIP file');
      return;
    }
    
    setBulkUploading(true);
    setBulkResults(null);
    
    try {
      const formData = new FormData();
      formData.append('period_start', bulkUploadData.period_start);
      formData.append('period_end', bulkUploadData.period_end);
      formData.append('zip_file', bulkUploadData.file);
      
      const response = await axios.post(`${API_URL}/statements/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setBulkResults(response.data);
      toast.success(`${response.data.uploaded.length} statements uploaded successfully`);
      fetchStatements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process bulk upload');
    } finally {
      setBulkUploading(false);
    }
  };

  const handlePreview = (statement) => {
    const ext = statement.file_name.split('.').pop().toLowerCase();
    if (ext === 'pdf' || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
      setPreviewUrl(`${API_URL}/documents/statement/${statement.id}/preview`);
      setPreviewType(ext === 'pdf' ? 'pdf' : 'image');
      setShowPreviewDialog(true);
    } else {
      // For other file types, download
      handleDownload(statement);
    }
  };

  const handleDownload = async (statement) => {
    try {
      const response = await axios.get(`${API_URL}/statements/${statement.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', statement.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download statement');
    }
  };

  const filteredStatements = statements.filter(statement =>
    statement.driver_name.toLowerCase().includes(search.toLowerCase()) ||
    statement.pay_run_reference?.toLowerCase().includes(search.toLowerCase()) ||
    statement.tripsheet_reference?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-statements">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Statements</h1>
            <p className="text-muted-foreground mt-1">Upload and manage driver statements</p>
          </div>
          
          <div className="flex gap-2">
            {/* Bulk Upload Dialog */}
            <Dialog open={showBulkUploadDialog} onOpenChange={setShowBulkUploadDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="bulk-upload-button">
                  <FolderArchive className="h-4 w-4 mr-2" />
                  Bulk Upload
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Bulk Upload Statements</DialogTitle>
                  <DialogDescription>
                    Upload a ZIP file containing statements. Files should be named with driver ID number, email prefix, or name for auto-matching.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleBulkUpload}>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="bulk_period_start">Period Start *</Label>
                        <Input
                          id="bulk_period_start"
                          type="date"
                          value={bulkUploadData.period_start}
                          onChange={(e) => setBulkUploadData({ ...bulkUploadData, period_start: e.target.value })}
                          required
                          data-testid="bulk-period-start"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bulk_period_end">Period End *</Label>
                        <Input
                          id="bulk_period_end"
                          type="date"
                          value={bulkUploadData.period_end}
                          onChange={(e) => setBulkUploadData({ ...bulkUploadData, period_end: e.target.value })}
                          required
                          data-testid="bulk-period-end"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="bulk_file">ZIP File *</Label>
                      <Input
                        id="bulk_file"
                        type="file"
                        accept=".zip"
                        onChange={(e) => setBulkUploadData({ ...bulkUploadData, file: e.target.files[0] })}
                        required
                        data-testid="bulk-file-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Name files with driver ID (e.g., 8501015026083.pdf) or name (e.g., john_smith.pdf)
                      </p>
                    </div>

                    {/* Bulk Upload Results */}
                    {bulkResults && (
                      <div className="space-y-3 p-4 bg-muted rounded-lg">
                        <p className="font-medium">Upload Results:</p>
                        {bulkResults.uploaded.length > 0 && (
                          <div>
                            <p className="text-sm text-green-600 flex items-center gap-1">
                              <CheckCircle className="h-4 w-4" />
                              {bulkResults.uploaded.length} uploaded successfully
                            </p>
                            <ul className="text-xs text-muted-foreground ml-5 mt-1">
                              {bulkResults.uploaded.slice(0, 5).map((u, i) => (
                                <li key={i}>{u.filename} → {u.driver}</li>
                              ))}
                              {bulkResults.uploaded.length > 5 && (
                                <li>...and {bulkResults.uploaded.length - 5} more</li>
                              )}
                            </ul>
                          </div>
                        )}
                        {bulkResults.failed.length > 0 && (
                          <div>
                            <p className="text-sm text-red-600 flex items-center gap-1">
                              <XCircle className="h-4 w-4" />
                              {bulkResults.failed.length} failed
                            </p>
                            <ul className="text-xs text-muted-foreground ml-5 mt-1">
                              {bulkResults.failed.map((f, i) => (
                                <li key={i}>{f.filename}: {f.reason}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => {
                      setShowBulkUploadDialog(false);
                      setBulkResults(null);
                    }}>
                      Close
                    </Button>
                    <Button type="submit" disabled={bulkUploading} data-testid="submit-bulk-upload">
                      {bulkUploading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : 'Upload ZIP'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* Single Upload Dialog */}
            <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
              <DialogTrigger asChild>
                <Button data-testid="upload-statement-button">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Statement
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Upload Statement</DialogTitle>
                  <DialogDescription>
                    Upload a pay statement for a driver. They will be notified automatically.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleUpload}>
                  <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                    <div className="space-y-2">
                      <Label htmlFor="driver">Driver *</Label>
                      <Select
                        value={uploadData.driver_id}
                        onValueChange={(value) => setUploadData({ ...uploadData, driver_id: value })}
                        required
                      >
                        <SelectTrigger data-testid="select-driver">
                          <SelectValue placeholder="Select a driver" />
                        </SelectTrigger>
                        <SelectContent>
                          {drivers.map((driver) => (
                            <SelectItem key={driver.id} value={driver.id}>
                              {driver.driver_name || driver.full_name || driver.driver_username || driver.email || 'Unnamed'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="period_start">Period Start *</Label>
                        <Input
                          id="period_start"
                          type="date"
                          value={uploadData.period_start}
                          onChange={(e) => setUploadData({ ...uploadData, period_start: e.target.value })}
                          required
                          data-testid="period-start-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="period_end">Period End *</Label>
                        <Input
                          id="period_end"
                          type="date"
                          value={uploadData.period_end}
                          onChange={(e) => setUploadData({ ...uploadData, period_end: e.target.value })}
                          required
                          data-testid="period-end-input"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="pay_run_reference">Pay Run Reference</Label>
                        <Input
                          id="pay_run_reference"
                          value={uploadData.pay_run_reference}
                          onChange={(e) => setUploadData({ ...uploadData, pay_run_reference: e.target.value })}
                          placeholder="e.g., PR-2024-001"
                          data-testid="pay-run-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="tripsheet_reference">Tripsheet Reference</Label>
                        <Input
                          id="tripsheet_reference"
                          value={uploadData.tripsheet_reference}
                          onChange={(e) => setUploadData({ ...uploadData, tripsheet_reference: e.target.value })}
                          placeholder="e.g., TS-2024-001"
                          data-testid="tripsheet-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="amount">Amount (ZAR)</Label>
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        value={uploadData.amount}
                        onChange={(e) => setUploadData({ ...uploadData, amount: e.target.value })}
                        placeholder="e.g., 15000.00"
                        data-testid="amount-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        value={uploadData.notes}
                        onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
                        placeholder="Additional notes..."
                        rows={2}
                        data-testid="notes-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="file">Statement File *</Label>
                      <Input
                        id="file"
                        type="file"
                        accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.gif,.webp"
                        onChange={(e) => setUploadData({ ...uploadData, file: e.target.files[0] })}
                        required
                        data-testid="file-input"
                      />
                      <p className="text-xs text-muted-foreground">Accepted: PDF, Excel, CSV, Images</p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowUploadDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={uploading} data-testid="submit-upload">
                      {uploading ? 'Uploading...' : 'Upload Statement'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by driver name or reference..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-statements-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Statements Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Statements</CardTitle>
            <CardDescription>
              {filteredStatements.length} statement{filteredStatements.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : filteredStatements.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                <h3 className="text-lg font-medium mb-2">No statements found</h3>
                <p className="text-muted-foreground">Upload a statement to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Driver</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Pay Run Ref</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Uploaded</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStatements.map((statement) => (
                      <TableRow key={statement.id} data-testid={`statement-row-${statement.id}`}>
                        <TableCell className="font-medium">{statement.driver_name}</TableCell>
                        <TableCell>{statement.period_start} - {statement.period_end}</TableCell>
                        <TableCell>{statement.pay_run_reference || '-'}</TableCell>
                        <TableCell>
                          {statement.amount ? `R${statement.amount.toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(statement.uploaded_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePreview(statement)}
                              title="Preview"
                              data-testid={`preview-${statement.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(statement)}
                              title="Download"
                              data-testid={`download-${statement.id}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preview Dialog */}
        <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Document Preview</DialogTitle>
            </DialogHeader>
            <div className="h-[70vh]">
              {previewType === 'pdf' ? (
                <iframe
                  src={previewUrl}
                  className="w-full h-full border rounded"
                  title="PDF Preview"
                />
              ) : (
                <img
                  src={previewUrl}
                  alt="Document Preview"
                  className="max-w-full max-h-full mx-auto object-contain"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
